import { useCallback, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  CalendarDays,
  ChevronDown,
  Layers3,
  RotateCcw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useWavePlanningStore } from '@/stores/useWavePlanningStore'
import {
  REFERENCE_WEEKS,
  WAVE_CALENDARS,
  WAVE_FIXTURES,
} from '@/components/WavePlanning/data'
import {
  formatPercent,
  formatUnits,
  shiftWaveDate,
} from '@/components/WavePlanning/model'
import { SKU_WAVE_FIXTURES } from '@/components/WavePlanning/sku-data'
import {
  calculateSkuPlanning,
  type SkuWaveComment,
  type SkuWaveDrafts,
  type SkuWaveLine,
} from '@/components/WavePlanning/sku-model'
import { SkuWaveGrid } from '@/components/WavePlanning/SkuWaveGrid'
import { SkuCommentsDrawer } from '@/components/WavePlanning/SkuCommentsDrawer'
import { WavePlanningNav } from '@/components/WavePlanning/WavePlanningNav'
import {
  getWavePlan,
  wavePlanPath,
  type WavePlan,
} from '@/components/WavePlanning/plans'
import { WavePlansBackLink } from '@/components/WavePlanning/WavePlansBackLink'

const EMPTY_DRAFTS: SkuWaveDrafts = {}
const EMPTY_COMMENTS: Record<string, SkuWaveComment[]> = {}
const fullDate = (value: string) =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`))

export default function WavePlanningDetail() {
  const { planId } = useParams()
  const plan = getWavePlan(planId)
  if (!plan) return <Navigate to="/wave-planning" replace />
  return <SkuDetail key={plan.id} plan={plan} />
}

function SkuDetail({ plan }: { plan: WavePlan }) {
  const { calendarId, subclassId } = plan
  const state = useWavePlanningStore()
  const { updateSkuWave, resetSkuChanges, addSkuComment, deleteSkuComment } =
    state
  const calendar = WAVE_CALENDARS.find(item => item.id === calendarId)!
  const overview = WAVE_FIXTURES.find(item => item.id === subclassId)!
  const fixture = SKU_WAVE_FIXTURES.find(item => item.id === subclassId)!
  const key = `${calendarId}:${subclassId}`
  const overall = state.drafts[key] ?? overview.plannedWaves
  const drafts = state.skuDrafts[key] ?? EMPTY_DRAFTS
  const comments = state.skuComments[key] ?? EMPTY_COMMENTS
  const [commentLine, setCommentLine] = useState<SkuWaveLine | null>(null)
  const commentTrigger = useRef<HTMLButtonElement | null>(null)
  const { userId, name } = useCurrentUser()
  const authorId = userId ?? 'wave-demo-user'
  const weeks = useMemo(
    () =>
      REFERENCE_WEEKS.map(week => ({
        ...week,
        date: shiftWaveDate(week.date, calendar.offsetDays),
      })),
    [calendar.offsetDays]
  )
  const result = useMemo(
    () => calculateSkuPlanning(fixture, weeks, overall, drafts),
    [fixture, weeks, overall, drafts]
  )
  const counts = useMemo(
    () =>
      Object.fromEntries(
        fixture.lines.map(line => [
          line.id,
          (comments[line.id]?.length ?? 0) + (line.workbookNote ? 1 : 0),
        ])
      ),
    [fixture.lines, comments]
  )
  const edit = useCallback(
    (lineId: string, index: number, percent: string) =>
      updateSkuWave(calendarId, subclassId, lineId, index, percent),
    [calendarId, subclassId, updateSkuWave]
  )
  const resetRow = useCallback(
    (lineId: string) => resetSkuChanges(calendarId, subclassId, lineId),
    [calendarId, subclassId, resetSkuChanges]
  )
  const openComments = useCallback(
    (line: SkuWaveLine, trigger: HTMLButtonElement) => {
      commentTrigger.current = trigger
      setCommentLine(line)
    },
    []
  )
  const reviewCount = result.rows.filter(row => row.needsReview).length
  const outside = result.visibleWeeks.filter(week => week.outside)
  const reference = [
    ['Subclass', overview.code],
    ['Description', fixture.description],
    ['Set date', fullDate(shiftWaveDate(fixture.setDate, calendar.offsetDays))],
    [
      'Exit date',
      fullDate(shiftWaveDate(fixture.exitDate, calendar.offsetDays)),
    ],
    ['POG description', fixture.pogDescription],
    ['Location', fixture.location],
    ['Status', fixture.status || '—'],
    ['Store count', formatUnits(fixture.stores)],
    ['# of DCs to ship', formatUnits(fixture.dcCount)],
    ['Sellable SKU count', formatUnits(fixture.sellableCount)],
    ['Shipping SKU count', formatUnits(fixture.shippingCount)],
    ['PIAB SKU count', formatUnits(fixture.piabCount)],
    ['CICO SKU count', formatUnits(fixture.cicoCount)],
    [
      'Repack SKU count (Replen + Single SKUs)',
      formatUnits(fixture.repackCount),
    ],
    ['Repack ship qty', formatUnits(fixture.repackShipQty)],
    [
      'Wave total',
      result.summary.totalPercent == null
        ? '—'
        : formatPercent(result.summary.totalPercent),
    ],
    ['Total units', formatUnits(result.summary.allocatedQty)],
    ['Overall purchase units', formatUnits(overview.purchaseUnits)],
  ]
  return (
    <div className="flex min-w-0 flex-col gap-4 p-4 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <WavePlansBackLink />
          <h1 className="text-2xl font-semibold">SKU Wave Planning</h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays size={13} /> {calendar.label}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Layers3 size={13} />{' '}
              <span className="font-medium text-foreground">
                {subclassId} · {plan.name}
              </span>
            </span>
          </div>
        </div>
        <div className="flex flex-col items-start gap-2 sm:items-end">
          <p className="text-[11px] text-muted-foreground">
            Demo data · Changes reset on reload
          </p>
          <Button
            size="sm"
            variant="outline"
            disabled={!Object.keys(drafts).length}
            onClick={() => resetSkuChanges(calendarId, subclassId)}
          >
            <RotateCcw size={14} /> Reset SKU changes
          </Button>
        </div>
      </header>
      <WavePlanningNav planId={plan.id} />
      <Card className="gap-0 overflow-hidden py-0 shadow-none">
        <div className="grid grid-cols-2 divide-x divide-border sm:grid-cols-4">
          {[
            {
              label: 'SKU total units',
              value: formatUnits(result.summary.totalQty),
              hint: `${fixture.lines.length} lines · ${fixture.sellableCount} sellable SKUs`,
              id: 'sku-total-units',
            },
            {
              label: 'Allocated units',
              value: formatUnits(result.summary.allocatedQty),
              hint: 'Across all wave dates',
              id: 'sku-allocated-units',
            },
            {
              label: 'Allocated',
              value:
                result.summary.totalPercent == null
                  ? '—'
                  : formatPercent(result.summary.totalPercent),
              hint: 'Weighted by SKU quantity',
              id: 'sku-allocated-percent',
            },
            {
              label: 'Lines to review',
              value: String(reviewCount),
              hint: reviewCount
                ? 'Check totals or dates outside plan'
                : 'All lines allocated to 100%',
              id: 'sku-review-count',
            },
          ].map(item => (
            <div key={item.id} className="px-4 py-3">
              <p className="text-[11px] font-medium text-muted-foreground">
                {item.label}
              </p>
              <p
                data-testid={item.id}
                className={`mt-0.5 text-xl font-semibold tabular-nums ${item.id === 'sku-review-count' && reviewCount ? 'text-amber-800' : ''}`}
              >
                {item.value}
              </p>
              <p className="mt-0.5 text-[10px] text-muted-foreground">
                {item.hint}
              </p>
            </div>
          ))}
        </div>
        <details className="group border-t" data-testid="sku-reference-details">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-2 text-xs text-muted-foreground hover:bg-secondary/40">
            <span>
              Reference details{' '}
              <span className="ml-2 hidden text-[11px] sm:inline">
                {fixture.description}
              </span>
            </span>
            <ChevronDown
              size={14}
              className="transition-transform group-open:rotate-180"
            />
          </summary>
          <h2 className="border-t bg-secondary/20 px-4 pt-3 text-xs font-semibold">
            DPT {overview.code.split('-')[0]} BY SKU WAVE BREAK
          </h2>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 bg-secondary/20 p-4 sm:grid-cols-3 lg:grid-cols-5">
            {reference.map(([label, value]) => (
              <div key={label}>
                <dt className="text-[11px] text-muted-foreground">{label}</dt>
                <dd className="mt-1 text-xs font-medium">{value}</dd>
              </div>
            ))}
          </dl>
          <p className="px-4 pb-3 text-[11px] text-muted-foreground">
            SKU quantities reflect the detail workbook. Overall purchase units
            are a separate planning reference.
            {calendar.offsetDays > 0 &&
              ' 2027 uses 2026 demo quantities with dates shifted by 52 weeks.'}
          </p>
        </details>
      </Card>
      <Card className="min-w-0 gap-0 overflow-hidden py-0 shadow-none">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2.5">
          <h2 className="text-sm font-semibold">SKU allocations</h2>
          <p className="text-[11px] text-muted-foreground">
            Starts from the overall plan · Edit each wave independently
          </p>
        </div>
        {outside.length > 0 && (
          <p
            role="status"
            className="flex items-center gap-2 border-b bg-amber-50 px-3 py-2 text-xs text-amber-900"
          >
            <AlertTriangle size={14} className="shrink-0" />{' '}
            {outside.map(week => week.event).join(', ')}{' '}
            {outside.length > 1 ? 'are' : 'is'} outside the overall plan.
            Retained SKU allocations still count; set them to zero or reset the
            rows to remove the date.
          </p>
        )}
        {result.visibleWeeks.length > 0 ? (
          <SkuWaveGrid
            rows={result.rows}
            weeks={result.visibleWeeks}
            commentCounts={counts}
            onEdit={edit}
            onResetRow={resetRow}
            onOpenComments={openComments}
          />
        ) : (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <CalendarDays size={30} className="text-muted-foreground" />
            <h3 className="text-sm font-medium">No wave dates yet</h3>
            <p className="max-w-sm text-xs text-muted-foreground">
              Add a percentage to a week in the overall plan to start
              distributing products.
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to={wavePlanPath(plan.id)}>Go to overall plan</Link>
            </Button>
          </div>
        )}
      </Card>
      {commentLine && (
        <SkuCommentsDrawer
          key={commentLine.id}
          line={commentLine}
          comments={comments[commentLine.id] ?? []}
          authorId={authorId}
          onClose={() => setCommentLine(null)}
          onPost={content =>
            addSkuComment(
              calendarId,
              subclassId,
              commentLine.id,
              content,
              authorId,
              name || 'Demo user'
            )
          }
          onDelete={id =>
            deleteSkuComment(
              calendarId,
              subclassId,
              commentLine.id,
              id,
              authorId
            )
          }
          onRestoreFocus={() => {
            ;(
              document.getElementById(`sku-comments-${commentLine.id}`) ??
              commentTrigger.current
            )?.focus()
          }}
        />
      )}
    </div>
  )
}
